import { useCallback, useContext } from 'react';
import { Socket } from 'socket.io-client';
import IProcessPOShipmentItemDTO from '../../models/DTO/IProcessPOShipmentItemDTO';
import IPO_Shipment from '../../models/IPO_Shipment';
import IPO_Shipment_Catalog_Item_MapUI from '../../models/IPO_Shipment_Catalog_Item_MapUI';
import IUserUI from '../../models/IUserUI';
import ISupplierModel from '../../models/ISupplierModel';
import EndpointsEnum from '../../models/EndpointsEnum';
import { ChangeEvent } from 'react';
import { SocketContext } from '@contexts/SocketContext';
import SupplierContext from '@contexts/SupplierContext';
import UserContext from '@contexts/UserContext';

interface UsePurchaseOrderShipmentQuantityProps {
    poShipment?: IPO_Shipment;
    setPoShipmentItems: (items: IPO_Shipment_Catalog_Item_MapUI[] | ((prev: IPO_Shipment_Catalog_Item_MapUI[] | undefined) => IPO_Shipment_Catalog_Item_MapUI[] | undefined)) => void;
    setIsFormDirty: (value: boolean) => void;
}

export const usePurchaseOrderShipmentQuantity = ({
    poShipment,
    setPoShipmentItems,
    setIsFormDirty
}: UsePurchaseOrderShipmentQuantityProps) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;

    const getTotalOrdered = useCallback((row: IPO_Shipment_Catalog_Item_MapUI): number => {
        let totalOrdered = 0;

        if (poShipment?.PO_Shipment_Purchase_Order_Maps) {
            for (const poShipmentPOMap of poShipment.PO_Shipment_Purchase_Order_Maps) {
                if (poShipmentPOMap.Purchase_Order?.Purchase_Order_Items) {
                    for (const poItem of poShipmentPOMap.Purchase_Order.Purchase_Order_Items) {
                        if (poItem.catalog_item_id === row.catalog_item_id) {
                            totalOrdered += poItem.qty;
                            totalOrdered -= poItem.qty_received || 0;
                        }
                    }
                }
            }
        }

        return totalOrdered;
    }, [poShipment]);

    const copyQtyToIncoming = useCallback((row: IPO_Shipment_Catalog_Item_MapUI) => {
        setPoShipmentItems(prevItems => {
            if (!prevItems) return prevItems;
            
            return prevItems.map(item => {
                if (item.catalog_item_id === row.catalog_item_id) {
                    return {
                        ...item,
                        qty_received: getTotalOrdered(row),
                        isDirty: true
                    };
                }
                return item;
            });
        });
        setIsFormDirty(true);
    }, [getTotalOrdered, setPoShipmentItems, setIsFormDirty]);

    const handleIncomingChange = useCallback((e: ChangeEvent<any>, row: IPO_Shipment_Catalog_Item_MapUI) => {
        setPoShipmentItems(prevItems => {
            if (!prevItems) return prevItems;
            
            return prevItems.map(item => {
                if (item.catalog_item_id === row.catalog_item_id) {
                    return {
                        ...item,
                        qty_received: Number(e.target.value),
                        isDirty: true
                    };
                }
                return item;
            });
        });
        setIsFormDirty(true);
    }, [setPoShipmentItems, setIsFormDirty]);

    const handleWarehouseChange = useCallback((e: ChangeEvent<any>, row: IPO_Shipment_Catalog_Item_MapUI) => {
        setPoShipmentItems(prevItems => {
            if (!prevItems) return prevItems;
            
            return prevItems.map(item => {
                if (item.catalog_item_id === row.catalog_item_id) {
                    return {
                        ...item,
                        warehouse_id: Number(e.target.value),
                        isDirty: true
                    };
                }
                return item;
            });
        });
        setIsFormDirty(true);
    }, [setPoShipmentItems, setIsFormDirty]);

    const savePOShipmentItem = useCallback((row: IPO_Shipment_Catalog_Item_MapUI) => {
        const payload: IProcessPOShipmentItemDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            PO_Shipment_Item: {
                catalog_item_id: row.catalog_item_id,
                po_shipment_id: row.po_shipment_id,
                qty_received: row.qty_received,
                warehouse_id: row.warehouse_id,
                guid: row.guid,
                id: row.id,
            },
        };

        socket.emit(EndpointsEnum.PurchaseOrderProcessPOShipmentItem, payload);
    }, [userModel.guid, userModel.token, supplierModel.guid, socket]);

    return {
        getTotalOrdered,
        copyQtyToIncoming,
        handleIncomingChange,
        handleWarehouseChange,
        savePOShipmentItem
    };
}; 